import { useState, useCallback, useEffect } from 'react'

/**
 * Core selection state and operations, decoupled from data source.
 *
 * Consumer provides getVisibleIds() to supply the current ordered set of visible IDs.
 */
export default function useSelectionState({
  singleSelect = false,
  maxSelection = 100,
  getVisibleIds = () => [],
  initialSelectedIds = []
} = {}) {
  const [selectedItems, setSelectedItems] = useState(() => new Set(initialSelectedIds || []))
  const [selectAllState, setSelectAllState] = useState('none') // 'none' | 'some' | 'all'
  const [lastAnchorId, setLastAnchorId] = useState(null)

  const updateAggregate = useCallback((selectedSet) => {
    const visibleIds = Array.isArray(getVisibleIds) ? getVisibleIds : getVisibleIds?.()
    const ids = typeof visibleIds === 'function' ? visibleIds() : (visibleIds || [])
    const total = ids.length

    if (!total || selectedSet.size === 0) {
      setSelectAllState('none')
    } else if (selectedSet.size === total) {
      setSelectAllState('all')
    } else {
      setSelectAllState('some')
    }
  }, [getVisibleIds])

  const toggleItem = useCallback((itemId, opts = {}) => {
    const { shiftKey = false, orderedIds: localOrderedIds } = opts || {}

    setSelectedItems(prev => {
      const prevSet = prev
      const newSet = new Set(prevSet)

      // resolve visible order (for range selection)
      const resolvedOrder = Array.isArray(localOrderedIds) && localOrderedIds.length > 0
        ? localOrderedIds
        : (getVisibleIds?.() || [])

      // Single select mode: toggle/replace and ignore shift
      if (singleSelect) {
        if (newSet.has(itemId)) {
          newSet.delete(itemId)
        } else {
          newSet.clear()
          newSet.add(itemId)
        }
        setLastAnchorId(itemId)
        updateAggregate(newSet)
        return newSet
      }

      // Range selection with Shift
      if (shiftKey && lastAnchorId && resolvedOrder.includes(lastAnchorId) && resolvedOrder.includes(itemId)) {
        const startIdx = resolvedOrder.indexOf(lastAnchorId)
        const endIdx = resolvedOrder.indexOf(itemId)
        const from = Math.min(startIdx, endIdx)
        const to = Math.max(startIdx, endIdx)
        const rangeIds = resolvedOrder.slice(from, to + 1)

        for (const id of rangeIds) {
          if (newSet.size >= maxSelection) break
          newSet.add(id)
        }
      } else {
        // Toggle single item
        if (newSet.has(itemId)) {
          newSet.delete(itemId)
        } else {
          if (newSet.size >= maxSelection) {
            console.warn(`Maximum ${maxSelection} items can be selected`)
            // keep previous set and aggregate state unchanged
            return prevSet
          }
          newSet.add(itemId)
        }
      }

      setLastAnchorId(itemId)
      updateAggregate(newSet)
      return newSet
    })
  }, [getVisibleIds, lastAnchorId, maxSelection, singleSelect, updateAggregate])

  const selectAll = useCallback(() => {
    const ids = getVisibleIds?.() || []

    if (selectAllState === 'all') {
      // Deselect all
      setSelectedItems(new Set())
      setSelectAllState('none')
      setLastAnchorId(null)
      return
    }

    const limited = ids.slice(0, maxSelection)
    const allSet = new Set(limited)

    setSelectedItems(allSet)
    updateAggregate(allSet) // will become 'all' only if limited.length === ids.length
  }, [getVisibleIds, maxSelection, selectAllState, updateAggregate])

  const clear = useCallback(() => {
    setSelectedItems(new Set())
    setSelectAllState('none')
    setLastAnchorId(null)
  }, [])

  const selectedCount = selectedItems.size

  // Ensure aggregate state reflects initial and subsequent changes
  useEffect(() => {
    updateAggregate(selectedItems)
  }, [selectedItems, updateAggregate])

  const recomputeAggregate = useCallback(() => {
    updateAggregate(selectedItems)
  }, [selectedItems, updateAggregate])

  return {
    // state
    selectedItems,
    selectedCount,
    selectAllState,
    lastAnchorId,

    // operations
    toggleItem,
    selectAll,
    clear,
    recomputeAggregate
  }
}